import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Sparkles, CalendarClock, BarChart3, Wand2, ShieldCheck, Star, ArrowRight,
} from 'lucide-react';
import {
  FaInstagram, FaXTwitter, FaLinkedin, FaFacebook, FaTiktok, FaYoutube, FaPinterest, FaThreads, FaBluesky,
} from 'react-icons/fa6';
import { SiteFooter } from '../components/SiteFooter';
import { useSeo } from '../lib/seo';

/**
 * Mobile-first landing for paid (Meta/TikTok) ad traffic. Deliberately lean and
 * distraction-free: minimal header (no mega-menu), one clear CTA repeated, social
 * proof, and a sticky bottom CTA on mobile. Send your ad's destination URL here.
 */

const NETWORKS = [
  { Icon: FaInstagram, color: '#E4405F' },
  { Icon: FaXTwitter, color: '#0F1419' },
  { Icon: FaLinkedin, color: '#0A66C2' },
  { Icon: FaFacebook, color: '#1877F2' },
  { Icon: FaTiktok, color: '#010101' },
  { Icon: FaYoutube, color: '#FF0000' },
  { Icon: FaPinterest, color: '#BD081C' },
  { Icon: FaThreads, color: '#000000' },
  { Icon: FaBluesky, color: '#0085FF' },
];

const BENEFITS = [
  { Icon: CalendarClock, title: 'Programa una vez, publica en todas', desc: 'Calendario visual y cola inteligente. Carga tu contenido y olvídate.' },
  { Icon: Wand2, title: 'La IA escribe por ti', desc: 'Captions, ideas y hashtags adaptados a cada red en segundos.' },
  { Icon: BarChart3, title: 'Métricas de todas tus redes', desc: 'Mide qué funciona desde un único panel, sin saltar entre apps.' },
  { Icon: ShieldCheck, title: 'Sin permanencia', desc: 'Prueba 3 días y cancela cuando quieras, en 1 clic.' },
];

const REVIEWS: { rating: number; quote: string; author: string }[] = [
  { rating: 5, quote: 'Antes tardaba toda la mañana en programar la semana. Ahora lo dejo listo en 20 minutos y la IA me escribe los textos.', author: 'Community manager freelance' },
  { rating: 4.5, quote: 'El calendario y la cola inteligente son lo mejor: arrastro los posts y se publican solos en mi mejor hora.', author: 'Marca de ecommerce' },
  { rating: 5, quote: 'Publico en Instagram, TikTok y LinkedIn a la vez, cada red con su propio texto. Me ahorra un montón de horas.', author: 'Creador de contenido' },
  { rating: 4, quote: 'Las analíticas unificadas me dejan ver qué funciona sin entrar a cada app. Echaba de menos algún gráfico más, pero genial.', author: 'Agencia de marketing' },
  { rating: 4.5, quote: 'El flujo de aprobación con enlace para mis clientes me ha quitado el lío de WhatsApp. Aprueban o piden cambios y listo.', author: 'Agencia social media' },
  { rating: 5, quote: 'Conectar Zettapost a ChatGPT y publicar hablando con la IA me parece de otro nivel.', author: 'Freelance de marketing' },
];

const FAQS = [
  { q: '¿En qué redes puedo publicar?', a: 'Instagram, X, LinkedIn, Facebook, TikTok, YouTube, Threads, Pinterest y Bluesky.' },
  { q: '¿Cómo es la prueba?', a: 'Eliges plan y empiezas 3 días de prueba. Hoy no se te cobra y puedes cancelar cuando quieras antes de que termine.' },
  { q: '¿Necesito saber de tecnología?', a: 'No. Conectas tus cuentas en un par de clics y empiezas a programar al instante.' },
];

function Cta({ label = 'Empieza tu prueba de 3 días', className = '' }: { label?: string; className?: string }) {
  return (
    <Link
      to="/register"
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-3.5 text-base shadow-sm transition ${className}`}
    >
      {label}
      <ArrowRight size={18} />
    </Link>
  );
}

/** Star rating with real fractional fill (supports 4, 4.5, etc.). */
function Stars({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} de 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const pct = Math.max(0, Math.min(1, rating - i)) * 100;
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} className="absolute inset-0 text-slate-300" />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
              <Star size={size} className="text-amber-400" fill="currentColor" />
            </span>
          </span>
        );
      })}
    </span>
  );
}

/** Lazy YouTube embed — loads iframe only after user clicks play. */
function YoutubePlayer({ videoId, thumbnail }: { videoId: string; thumbnail: string }) {
  const [active, setActive] = useState(false);
  return (
    <div className="relative w-full rounded-2xl overflow-hidden shadow-lg bg-black" style={{ aspectRatio: '16/9' }}>
      {active ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`}
          title="Demo Zettapost"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          className="absolute inset-0 w-full h-full group focus:outline-none"
          aria-label="Reproducir demo"
        >
          <img
            src={thumbnail}
            alt="Vista previa del vídeo"
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* dark overlay */}
          <span className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition" />
          {/* play button */}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-16 w-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-xl transition">
              <svg viewBox="0 0 24 24" className="h-7 w-7 text-brand-600 fill-current ml-1" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </span>
        </button>
      )}
    </div>
  );
}

/** Auto-rotating reviews carousel. */
function ReviewSlider() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % REVIEWS.length), 5000);
    return () => clearInterval(t);
  }, []);
  const r = REVIEWS[idx]!;
  return (
    <div className="max-w-md mx-auto text-center">
      <div className="min-h-[200px] sm:min-h-[170px] flex flex-col items-center justify-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Stars rating={r.rating} size={18} />
          <span className="text-sm font-semibold text-slate-700 tabular-nums">
            {r.rating.toLocaleString('es-ES')}
          </span>
        </div>
        <p key={idx} className="text-lg font-medium text-slate-800 leading-relaxed animate-fade-in-up">
          "{r.quote}"
        </p>
        <p className="mt-3 text-sm text-slate-500">— {r.author}</p>
      </div>
      <div className="mt-4 flex items-center justify-center gap-1.5">
        {REVIEWS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            aria-label={`Reseña ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-brand-600' : 'w-1.5 bg-slate-300 hover:bg-slate-400'}`}
          />
        ))}
      </div>
    </div>
  );
}

export function LandingAds() {
  useSeo({
    title: 'Programa todas tus redes sociales con IA · Zettapost',
    description:
      'Crea, programa y publica en 9 redes sociales desde un solo lugar, con IA que escribe por ti. Prueba 3 días, cancela cuando quieras.',
    canonical: 'https://zettapost.com/lp',
  });

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      {/* minimal header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-5 h-14 flex items-center justify-between">
          <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-7 w-auto" />
          <Link to="/login" className="text-sm font-medium text-slate-500 hover:text-slate-900">
            Iniciar sesión
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* hero */}
        <section className="px-5 pt-10 pb-8 text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 bg-brand-50 border border-brand-100 rounded-full px-3 py-1">
            <Sparkles size={13} /> Programador de redes con IA
          </span>
          <h1 className="mt-4 text-2xl sm:text-3xl font-extrabold leading-tight tracking-tight">
            Programa contenidos en todas tus redes para todo el mes en minutos, no en horas
          </h1>
          <div className="mt-5">
            <YoutubePlayer videoId="ulXVOcgeElo" thumbnail="/zp-demo.webp" />
          </div>

          <p className="mt-3 text-[17px] text-slate-600 leading-relaxed">
            Crea, programa y publica en <strong>9 redes</strong> desde un solo sitio. Con IA que escribe por ti.
          </p>

          <div className="mt-6 flex flex-col items-center gap-2">
            <Cta className="w-full sm:w-auto" />
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <CheckCircle2 size={14} className="text-emerald-500" /> Cancela cuando quieras · sin permanencia
            </span>
          </div>

          {/* social proof */}
          <div className="mt-7 flex flex-col items-center gap-1.5">
            <img src="/images-users.webp" alt="" className="h-9 w-auto" loading="lazy" />
            <span className="inline-flex text-amber-400">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={15} fill="currentColor" />)}
            </span>
            <p className="text-sm text-slate-600 max-w-[16rem]">
              Marcas y creadores ya programan con Zettapost
            </p>
          </div>

          {/* networks */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-1.5">
            {NETWORKS.map(({ Icon, color }, i) => (
              <span key={i} className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <Icon size={14} color={color} />
              </span>
            ))}
          </div>
        </section>

        {/* benefits */}
        <section className="px-5 py-8 bg-slate-50 border-y border-slate-100">
          <div className="max-w-md mx-auto space-y-3">
            {BENEFITS.map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
                <span className="h-10 w-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                  <Icon size={18} />
                </span>
                <div>
                  <p className="font-bold text-[15px] leading-tight">{title}</p>
                  <p className="text-sm text-slate-500 mt-0.5 leading-snug">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* reviews slider */}
        <section className="px-5 py-9">
          <ReviewSlider />
        </section>

        {/* faq */}
        <section className="px-5 py-8 bg-slate-50 border-y border-slate-100">
          <div className="max-w-md mx-auto space-y-3">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl bg-white border border-slate-200 p-4">
                <p className="font-semibold text-[15px]">{f.q}</p>
                <p className="text-sm text-slate-500 mt-1 leading-snug">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* final cta */}
        <section className="px-5 py-12 text-center max-w-md mx-auto">
          <h2 className="text-2xl font-extrabold leading-tight">Empieza hoy mismo</h2>
          <p className="mt-2 text-slate-600">Conecta tus redes y deja tu semana programada en minutos.</p>
          <Cta className="mt-5 w-full" />
          <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 justify-center w-full">
            <CheckCircle2 size={14} className="text-emerald-500" /> 3 días de prueba · cancela cuando quieras
          </span>
        </section>
      </main>

      <SiteFooter />

      {/* sticky mobile CTA */}
      <div className="sm:hidden sticky bottom-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3">
        <Cta className="w-full" label="Empezar gratis" />
      </div>
    </div>
  );
}
